import { Probable, RaceWagerType } from "@tvg/ts-types/Race";
import {
  formatProbableAmountList,
  getBetCombosHash
} from "@tvg/sh-lib-my-bets/utils/potentialReturn";
import exactaAndDailyDouble from "@tvg/sh-lib-my-bets/utils/potentialReturn/exactaAndDailyDouble";
import { flatten, get, isEmpty, size } from "lodash";
import {
  WagerType,
  WagerTypeCodesEnum,
  WagerTypesPos
} from "@tvg/ts-types/Wager";
import quinella from "@tvg/sh-lib-my-bets/utils/potentialReturn/quinella";
import placeAndShow from "@tvg/sh-lib-my-bets/utils/potentialReturn/placeAndShow";
import win from "@tvg/sh-lib-my-bets/utils/potentialReturn/win";
import { VisualSelection, VisualSelections } from "@tvg/ts-types/Bet";

export const mapSelectionsForWPSProbables = (
  betSelections: string[][],
  wpsSelections: VisualSelections,
  wagerTypeCode?: WagerTypeCodesEnum
): string[][] => {
  // Will remove uneccessary bet selections for columns not used, eg: for win if the selections are [["1"],["2"]] the probables
  // calculation will be wrong it should be [["1"]]
  const usedColumns = {
    [WagerTypeCodesEnum.WIN]: [0],
    [WagerTypeCodesEnum.WIN_PLACE]: [0, 1],
    [WagerTypeCodesEnum.WIN_SHOW]: [0, 2],
    [WagerTypeCodesEnum.PLACE]: [1],
    [WagerTypeCodesEnum.PLACE_SHOW]: [1, 2],
    [WagerTypeCodesEnum.SHOW]: [2],
    [WagerTypeCodesEnum.WIN_PLACE_SHOW]: [0, 1, 2]
  };

  return betSelections.map((betSelection: string[], index: number) =>
    get(usedColumns, `${wagerTypeCode}`) &&
    get(usedColumns, `${wagerTypeCode}`, []).includes(index)
      ? betSelection.filter(
          (selection) =>
            // will filter the selections that are present for the specific bet Type eg: WP -> [["1,"2","3"],["1"]] should be [["1"],["1"]]
            !!wpsSelections
              .flat()
              .find(
                (wpsSelection: VisualSelection) =>
                  wpsSelection.number === selection
              )
        )
      : []
  );
};

export const getProbableValues = (
  probableIndex: number,
  probables: Probable[],
  betSelections: string[][],
  betAmount: number
): number[] => {
  if (probableIndex !== -1) {
    // Search and calculate probable
    const { wagerType, minWagerAmount, betCombos } = probables[probableIndex];

    // Turn betCombo into an hashTable
    const betCombosHash = getBetCombosHash(betCombos);

    // Find which payout we want to multiply
    const typesProps = {
      betSelections,
      betCombosHash,
      minWagerAmount,
      betAmount
    };

    switch (wagerType.code) {
      // Exacta
      case WagerTypeCodesEnum.EXACTA:
        return exactaAndDailyDouble(typesProps);
      // Daily Double
      case WagerTypeCodesEnum.DAILY_DOUBLE:
        return exactaAndDailyDouble(typesProps);
      // Quinella
      case WagerTypeCodesEnum.QUINELLA:
        return quinella(typesProps);
      case WagerTypeCodesEnum.SHOW:
        return placeAndShow({
          ...typesProps,
          wagerTypeId: wagerType.id
        });
      // Place
      case WagerTypeCodesEnum.PLACE:
        return placeAndShow({
          ...typesProps,
          wagerTypeId: wagerType.id
        });
      // Win
      case WagerTypeCodesEnum.WIN:
        return win(typesProps);
      default:
        return [];
    }
  }

  return [];
};

export const formatSelectionsArray = (
  wagerTypeId: string,
  betSelections: string[][]
): string[][] => {
  if (wagerTypeId === WagerTypeCodesEnum.WIN) {
    return [get(betSelections, `[${WagerTypesPos.Win}]`, [])];
  }
  if (wagerTypeId === WagerTypeCodesEnum.PLACE) {
    return [get(betSelections, `[${WagerTypesPos.Place}]`, [])];
  }
  if (wagerTypeId === WagerTypeCodesEnum.SHOW) {
    return [get(betSelections, `[${WagerTypesPos.Show}]`, [])];
  }

  return [];
};

export const checkSpecialGroupType = (
  betSelections: string[][]
): WagerTypeCodesEnum | WagerTypeCodesEnum[] => {
  let type;

  // GET SELECTIONS
  const winSelections = get(betSelections, `[${WagerTypesPos.Win}].length`, 0);
  const placeSelections = get(
    betSelections,
    `[${WagerTypesPos.Place}].length`,
    0
  );
  const showSelections = get(
    betSelections,
    `[${WagerTypesPos.Show}].length`,
    0
  );

  // Check if single wager type
  if (winSelections > 0 && placeSelections === 0 && showSelections === 0) {
    type = WagerTypeCodesEnum.WIN;
  } else if (
    placeSelections > 0 &&
    winSelections === 0 &&
    showSelections === 0
  ) {
    type = WagerTypeCodesEnum.PLACE;
  } else if (
    showSelections > 0 &&
    winSelections === 0 &&
    placeSelections === 0
  ) {
    type = WagerTypeCodesEnum.SHOW;
  } else {
    type = WagerTypeCodesEnum.WIN_PLACE_SHOW;
  }

  if (type === WagerTypeCodesEnum.WIN_PLACE_SHOW) {
    const includedRunners: string[] = [];
    betSelections.forEach((selection) => {
      selection.forEach((runner) => {
        if (!includedRunners.includes(runner)) {
          includedRunners.push(runner);
        }
      });
    });

    if (includedRunners.length <= 3) {
      let presentTypes: WagerTypeCodesEnum[] = [];
      const types: WagerTypeCodesEnum[] = [
        WagerTypeCodesEnum.WIN,
        WagerTypeCodesEnum.PLACE,
        WagerTypeCodesEnum.SHOW
      ];

      betSelections.forEach((selection: string[], index: number) => {
        if (selection.length > 0) {
          presentTypes = [...presentTypes, types[index]];
        }
      });
      return presentTypes;
    }
  }

  return type;
};

export const getCurrentProbableAmount = (
  probables: Probable[],
  betAmount: number,
  betSelections: string[][],
  isSpecialGroup: boolean = false,
  currentWagerType?: WagerType | RaceWagerType | null
): string => {
  let probableValueList: number[] = [];
  let betSelectionsOrganized = betSelections;

  if (
    !isEmpty(probables) &&
    !isEmpty(currentWagerType) &&
    betAmount &&
    !isEmpty(flatten(betSelections))
  ) {
    let probableIndex = -1;
    let specialGroupTypeId: WagerTypeCodesEnum | WagerTypeCodesEnum[] | null =
      null;

    // is the currentWagerType selected in the probables array
    if (isSpecialGroup) {
      specialGroupTypeId = checkSpecialGroupType(betSelectionsOrganized);
    } else {
      probableIndex = probables.findIndex(
        (probable: Probable) =>
          probable.wagerType.id === get(currentWagerType, "type.id") ||
          probable.wagerType.id === get(currentWagerType, "id")
      );
    }

    if (specialGroupTypeId && !Array.isArray(specialGroupTypeId)) {
      probableIndex = probables.findIndex(
        (probable: Probable) => probable.wagerType.code === specialGroupTypeId
      );

      betSelectionsOrganized = formatSelectionsArray(
        specialGroupTypeId,
        betSelections
      );
    }

    // If is combined wager we have to get all amounts and sum

    if (specialGroupTypeId && Array.isArray(specialGroupTypeId)) {
      const allProbables = specialGroupTypeId.map(
        (wagerTypeId: WagerTypeCodesEnum) => {
          const selections = formatSelectionsArray(wagerTypeId, betSelections);

          const probIndex = probables.findIndex(
            (probable: Probable) => probable.wagerType.code === wagerTypeId
          );

          return getProbableValues(probIndex, probables, selections, betAmount);
        }
      );

      // Check if has invalid probable
      if (allProbables.find((probable) => !size(probable))) {
        return "0";
      }

      let minOfMin = 999999;
      let maxOfWin = 0;
      let maxOfPlace = 0;
      let maxOfShow = 0;

      allProbables.forEach((probable, index) => {
        // Get biggest numbers for each wager
        if (index === WagerTypesPos.Win) {
          maxOfWin = Math.max(...probable);
        } else if (index === WagerTypesPos.Place) {
          maxOfPlace = Math.max(...probable);
        } else if (index === WagerTypesPos.Show) {
          maxOfShow = Math.max(...probable);
        }

        // Get lowest number but checking on each wager
        const currentMinNumber = Math.min(...probable);
        if (currentMinNumber < minOfMin) {
          minOfMin = currentMinNumber;
        }
      });

      const sumOfWagerTypes = maxOfWin + maxOfPlace + maxOfShow;
      probableValueList = [minOfMin, sumOfWagerTypes];
    } else {
      probableValueList = getProbableValues(
        probableIndex,
        probables,
        betSelectionsOrganized,
        betAmount
      );
    }

    return formatProbableAmountList(probableValueList, betAmount);
  }
  return "0";
};
